import { Card } from '@mui/material'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { History } from 'history'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import { List } from 'react-virtualized'

import { FilterOption } from '@/app/component/atom/filters/types'
import Loader from '@/app/component/atom/loader'
import { SIDEBAR_WIDTH } from '@/app/component/layout/sidebar-layout'
import {
  CALL_STATUS_FILTER,
  STATUS_FILTER,
  TOPUP_STATUS_FILTER,
  TYPE_FILTER,
  WHATSAPP_STATUS_FILTER,
} from '@/app/module/logs/definitions'
import {
  CallLogItem,
  LogDownloadArgs,
  LogNavigatePayload,
  LogRequestArgs,
  LogTab,
  MessageLogItem,
  TopupLogItem,
  WhatsAppLogItem,
} from '@/app/module/logs/helpers'
import { PersonalizationType } from '@/app/types'

import CallRow from './call-row'
import LoadingMessagesRow from './loading-message-row'
import MessageRow from './message-row'
import LogToolbar from './toolbar'
import TopupRow from './topup-row'
import WhatsappRow from './whatsapp-row'

const tabLabels: { [key: string]: string } = {
  whatsapp: 'WhatsApp messages',
  messages: 'messages',
  calls: 'calls',
  topups: 'top-ups',
}

type RowType = {
  key: string
  index: number
  style: React.CSSProperties
  isScrolling?: boolean
  isVisible?: boolean
}

type ScrollEvent = {
  clientHeight: number
  scrollHeight: number
  scrollTop: number
}

type LogListDataItem = MessageLogItem | CallLogItem | WhatsAppLogItem | TopupLogItem

type Props = {
  data: LogListDataItem[]
  endCursor: string
  error: string
  from: string
  hasNextPage: boolean
  history: History
  orgId: number
  pageSize: number
  personalizationList: PersonalizationType
  search: string
  status: string
  tab: LogTab
  timezone: string
  to: string
  token: string
  type: string

  downloadData: (args: LogDownloadArgs) => Promise<unknown> | void
  getData: (args: LogRequestArgs) => Promise<unknown>
  getCustomFields: (args: { orgId: number; token: string }) => Promise<unknown>
  navigateLogs: (payload: LogNavigatePayload) => void
  reloadData: () => void
}

const filterLabels = {
  messages: STATUS_FILTER,
  calls: CALL_STATUS_FILTER,
  whatsapp: WHATSAPP_STATUS_FILTER,
  topups: TOPUP_STATUS_FILTER,
}

const getFilters = (props: Props): Record<string, FilterOption> => {
  const { type, status, from, to, tab } = props
  const statusFilter = filterLabels[tab]
  return {
    ...(tab !== 'topups' && {
      type: {
        type: 'radio',
        name: 'Type',
        value: type,
        values: TYPE_FILTER.map(({ name, value }) => ({
          label: name,
          id: name,
          value: value.toString() || '',
          editable: true,
        })),
      },
    }),
    status: {
      type: 'radio',
      name: 'Status',
      value: status,
      values: statusFilter.map(({ name, value }) => ({
        label: name,
        id: name,
        value: value.toString() || '',
        editable: true,
      })),
    },
    fromto: {
      type: 'datetimerange',
      name: 'Date',
      value: {
        from: from && new Date(from).toISOString(),
        to: to && new Date(to).toISOString(),
      },
    },
  }
}

const LogList: React.FC<Props> = (props) => {
  const {
    data,
    endCursor,
    from,
    hasNextPage,
    history,
    orgId,
    pageSize,
    search: initialSearch = '',
    status,
    tab,
    to,
    token,
    type,

    downloadData,
    getData,
    getCustomFields,
    navigateLogs,
    reloadData: reloadDataProps,
  } = props
  const navigate = useNavigate()
  const filters = getFilters(props)
  const [windowWidth, setWindowWidth] = React.useState(0)
  const [windowHeight, setWindowHeight] = React.useState(0)
  const [loading, setLoading] = React.useState(true)
  const [search, setSearch] = React.useState(initialSearch)

  React.useEffect(() => {
    const updateWindowDimensions = () => {
      setWindowWidth(window.innerWidth)
      setWindowHeight(window.innerHeight)
    }
    let ref: number = 0
    updateWindowDimensions()
    window.addEventListener('resize', () => {
      if (ref === 0) {
        ref = requestAnimationFrame(() => {
          updateWindowDimensions()
          ref = 0
        })
      }
    })
  }, [])

  React.useEffect(() => {
    if (orgId && token) {
      getCustomFields({ orgId, token })
    }
  }, [orgId, token, getCustomFields])

  React.useEffect(() => {
    if (token && orgId) {
      setLoading(true)
      reloadDataProps()
      getData({
        token,
        orgId,
        query: {
          filters: {
            type,
            status,
            to,
            from,
          },
          pageSize,
          search,
        },
      }).then(() => {
        setLoading(false)
        setSearch(search)
      })
    }
  }, [from, orgId, pageSize, search, status, to, token, type, getData, reloadDataProps])

  const navigateTo = () => {
    if (token && orgId) {
      setLoading(true)
      getData({
        token,
        orgId,
        query: {
          filters: {
            type,
            status,
            to,
            from,
          },
          endCursor,
          pageSize,
          search,
        },
      }).then(() => setLoading(false))
    }
  }

  const onScroll = ({ clientHeight, scrollHeight, scrollTop }: ScrollEvent) => {
    const fetchHeight: number = scrollHeight - clientHeight - 500
    if (scrollTop > fetchHeight && !loading && hasNextPage) {
      navigateTo()
    }
  }

  const reloadData = () => {
    setLoading(true)
    props.reloadData()
    if (token && orgId) {
      getData({
        token,
        orgId,
        query: {
          filters: {
            type,
            status,
            to,
            from,
          },
          pageSize,
          search,
        },
      }).then(() => setLoading(false))
    }
  }

  const download = () => {
    if (token && orgId) {
      downloadData({
        token,
        orgId,
        query: {
          filters: {
            type,
            status,
            to,
            from,
          },
          search,
        },
      })
    }
  }

  const rowRenderer = (row: RowType) => {
    const { key, index, style } = row
    const isLoadingRow = !data[index]

    const viewContact = (contactId: number) => navigate(`/contacts/edit/${contactId}`)

    return (
      <RowContainer className="logs-list-row" key={key} style={style}>
        {isLoadingRow ? (
          <LoadingMessagesRow />
        ) : (
          <>
            {tab === 'messages' && (
              <MessageRow windowWidth={windowWidth} {...(data[index] as MessageLogItem)} viewContact={viewContact} />
            )}
            {tab === 'calls' && (
              <CallRow
                windowWidth={windowWidth}
                {...(data[index] as CallLogItem)}
                viewContact={viewContact}
                isLoadingRow={isLoadingRow}
              />
            )}
            {tab === 'whatsapp' &&
              (() => {
                const whatsappData = data[index] as WhatsAppLogItem
                return (
                  <WhatsappRow
                    {...whatsappData}
                    isLoadingRow={isLoadingRow}
                    part={whatsappData}
                    videoFiles={whatsappData.videoFiles ?? []}
                    windowWidth={windowWidth}
                    viewContact={viewContact}
                  />
                )
              })()}
            {tab === 'topups' && (
              <TopupRow {...(data[index] as TopupLogItem)} viewContact={viewContact} isLoadingRow={isLoadingRow} />
            )}
          </>
        )}
      </RowContainer>
    )
  }

  return (
    <Container>
      <Card sx={{ height: '100%' }}>
        <LogToolbar
          filters={filters}
          history={history}
          loading={loading}
          search={search}
          tab={tab}
          navigateLogs={navigateLogs}
          reloadData={reloadData}
          setSearch={setSearch}
          download={download}
        />
        {!!data.length && (
          <List
            className="log-list"
            height={windowHeight - 2 * 64 - 48}
            width={windowWidth - SIDEBAR_WIDTH - 32}
            style={{
              paddingTop: '16px',
              outline: 'none',
            }}
            rowCount={data.length + (loading ? 1 : 0)}
            rowHeight={getRowHeight(tab)}
            rowRenderer={rowRenderer}
            onScroll={onScroll}
          />
        )}
        {!data.length && !loading && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{
              py: 4,
            }}
          >
            No {tabLabels[tab]} were found.
          </Typography>
        )}
      </Card>
      {loading && <Loader />}
    </Container>
  )
}

const getRowHeight = (tab: string) => {
  switch (tab) {
    case 'topups':
      return 120
    case 'whatsapp':
      return 220
    default:
      return 160
  }
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
})

const RowContainer = styled('div')({
  margin: 'auto',
  left: '0',
  right: '0',
})

export default LogList
